import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "../../lib/api";
import PublicShell from "../../components/PublicShell";
import HouseImagePlaceholder from "../../components/HouseImagePlaceholder";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { formatMoney } from "../../lib/auth";
import { CalendarDays, Users, BedDouble, Bath, MapPin, Check } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { getTrafficSource } from "../../lib/brand";
import usePageSeo, { vacationRentalSchema } from "../../lib/seo";
import IntroVideoButton from "../../components/IntroVideoButton";

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function HouseDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [house, setHouse] = useState(null);
  const [blockedRanges, setBlockedRanges] = useState([]);

  usePageSeo({
    title: house?.name ? `${house.name} · ${house.city || "Pernambuco"}` : "Detalhe da casa",
    description: house?.tagline || house?.description || "Detalhes, disponibilidade e reserva direta pela Azuhomes.",
    path: `/casas/${slug || ""}`,
    image: (house?.images && house.images[0]) || undefined,
    jsonLd: house ? vacationRentalSchema(house) : null,
  });
  const [range, setRange] = useState({
    from: params.get("check_in") ? parseISO(params.get("check_in")) : undefined,
    to: params.get("check_out") ? parseISO(params.get("check_out")) : undefined,
  });
  const [guests, setGuests] = useState(Number(params.get("guests") || 2));
  const [availability, setAvailability] = useState(null);
  const [checkingAvail, setCheckingAvail] = useState(false);
  const [step, setStep] = useState("select"); // select | guest
  const [guestForm, setGuestForm] = useState({ name: "", email: "", phone: "", document: "" });
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [howHeard, setHowHeard] = useState("");
  const [lgpdConsent, setLgpdConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/houses/${slug}`).then((r) => setHouse(r.data)).catch(() => setHouse(false));
    api.get(`/public/houses/${slug}/blocked-dates`).then((r) => setBlockedRanges(r.data));
  }, [slug]);

  useEffect(() => {
    if (range?.from && range?.to && house) {
      setCheckingAvail(true);
      api
        .post("/public/availability", {
          house_id: house.id,
          check_in: toISO(range.from),
          check_out: toISO(range.to),
        })
        .then((r) => setAvailability(r.data))
        .catch(() => setAvailability(null))
        .finally(() => setCheckingAvail(false));
    } else {
      setAvailability(null);
    }
  }, [range, house]);

  if (house === false)
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto py-32 px-6 text-center">
          <h1 className="font-display text-4xl text-[#0AA89C]">Casa não encontrada</h1>
        </div>
      </PublicShell>
    );

  if (!house)
    return (
      <PublicShell>
        <div className="max-w-6xl mx-auto py-16 px-6 animate-pulse">
          <div className="h-96 bg-slate-100 rounded-lg" />
        </div>
      </PublicShell>
    );

  const disabledDates = [{ before: new Date() }, ...blockedRanges.map((b) => ({
    from: parseISO(b.start_date),
    to: new Date(parseISO(b.end_date).getTime() - 86400000),
  }))];

  async function submitReservation() {
    if (!range?.from || !range?.to) { toast.error("Selecione as datas"); return; }
    if (!guestForm.name || !guestForm.email || !guestForm.phone) { toast.error("Preencha nome, e-mail e telefone"); return; }
    if (!lgpdConsent) { toast.error("É necessário aceitar o tratamento de dados conforme a LGPD"); return; }
    setSubmitting(true);
    try {
      const totalGuests = Math.max(1, Number(adults || 0) + Number(children || 0));
      const { data: reservation } = await api.post("/public/reservations", {
        house_id: house.id,
        check_in: toISO(range.from),
        check_out: toISO(range.to),
        guests: totalGuests,
        adults: Number(adults),
        children: Number(children),
        guest: guestForm,
        traffic_source: getTrafficSource() || {},
        how_did_you_hear: howHeard || null,
        lgpd_consent: true,
      });
      try {
        const { data: pay } = await api.post("/public/payments/checkout", { reservation_id: reservation.id });
        if (pay.init_point) { window.location.href = pay.init_point; return; }
      } catch (payErr) {
        // PAYMENT_MODE=pix_only → checkout Pro retorna 410. Redirecionar para tela Pix dedicada.
        if (payErr?.response?.status === 410) {
          nav(`/reserva/${reservation.guest_link_token}/pix`);
          return;
        }
        toast.warning("Reserva criada. Pagamento online estará disponível em breve.");
      }
      nav(`/reserva/${reservation.guest_link_token}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar reserva");
    } finally {
      setSubmitting(false);
    }
  }

  const cover = (house.photos || []).find((p) => p.is_cover) || (house.photos || [])[0];
  const photos = house.photos || [];

  return (
    <PublicShell>
      {/* HERO PHOTOS */}
      <section className="px-6 lg:px-10 pt-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#066B63]">
              {house.city} / {house.state}
            </span>
            <h1 className="font-display text-4xl md:text-5xl text-[#0AA89C] mt-2">{house.name}</h1>
            {house.tagline && <p className="text-slate-600 mt-2">{house.tagline}</p>}
          </div>

          {photos.length === 0 ? (
            <HouseImagePlaceholder ratio="aspect-[16/7]" label="Fotos serão adicionadas pelo painel" />
          ) : (
            <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[420px]">
              <div className="col-span-4 md:col-span-2 row-span-2 overflow-hidden rounded-lg bg-slate-100">
                <img src={fileUrl(cover?.storage_path)} alt={house.name} className="w-full h-full object-cover" />
              </div>
              {photos.slice(1, 5).map((p) => (
                <div key={p.id} className="hidden md:block overflow-hidden rounded-lg bg-slate-100">
                  <img src={fileUrl(p.storage_path)} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          {house.intro_video && (
            <div className="mt-4 flex justify-end">
              <IntroVideoButton house={house} />
            </div>
          )}
        </div>
      </section>

      {/* CONTENT + BOOKING */}
      <section className="px-6 lg:px-10 py-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_420px] gap-12">
          <div>
            <div className="flex flex-wrap gap-6 pb-8 border-b border-slate-200 text-sm text-slate-700">
              <span className="flex items-center gap-2"><Users className="w-4 h-4" /> {house.max_guests} hóspedes</span>
              <span className="flex items-center gap-2"><BedDouble className="w-4 h-4" /> {house.bedrooms} quartos · {house.beds} camas</span>
              <span className="flex items-center gap-2"><Bath className="w-4 h-4" /> {house.bathrooms} banheiros</span>
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {house.city} / {house.state}</span>
            </div>

            <div className="py-8">
              <h2 className="font-display text-2xl text-[#0AA89C] mb-4">A casa</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">{house.description}</p>
            </div>

            {house.amenities?.length > 0 && (
              <div className="py-8 border-t border-slate-200">
                <h2 className="font-display text-2xl text-[#0AA89C] mb-6">Comodidades</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {house.amenities.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-[#0AA89C]" aria-hidden="true" />
                      <span>{a.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {house.house_rules && (
              <div className="py-8 border-t border-slate-200">
                <h2 className="font-display text-2xl text-[#0AA89C] mb-4">Regras da casa</h2>
                <p className="text-sm text-slate-600 whitespace-pre-line">{house.house_rules}</p>
              </div>
            )}
          </div>

          {/* Booking widget */}
          <aside className="lg:sticky lg:top-24 h-fit">
            <div
              data-testid="booking-widget"
              className="bg-white border border-slate-200 rounded-lg p-6 shadow-[0_20px_40px_-15px_rgba(26,54,93,0.12)]"
            >
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 block">A partir de</span>
                  <span className="font-display text-3xl text-[#0AA89C]" data-testid="booking-base-price">{formatMoney(house.base_price)}</span>
                  <span className="text-xs text-slate-500"> / noite</span>
                </div>
                <span className="text-xs text-slate-500">Mín. {house.min_nights} noite(s)</span>
              </div>

              {step === "select" && (
                <>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        data-testid="booking-date-trigger"
                        type="button"
                        className="w-full text-left px-4 py-3 border border-slate-300 rounded-md hover:border-[#0AA89C] transition-colors flex items-center gap-3"
                      >
                        <CalendarDays className="w-4 h-4" />
                        <span className="text-sm">
                          {range?.from
                            ? range?.to
                              ? `${format(range.from, "d MMM", { locale: ptBR })} — ${format(range.to, "d MMM", { locale: ptBR })}`
                              : `${format(range.from, "d MMM", { locale: ptBR })} — ?`
                            : "Selecione as datas"}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="end">
                      <Calendar
                        mode="range"
                        locale={ptBR}
                        selected={range}
                        onSelect={setRange}
                        numberOfMonths={2}
                        disabled={disabledDates}
                      />
                    </PopoverContent>
                  </Popover>

                  <div className="mt-3">
                    <Label className="text-xs uppercase tracking-[0.22em] text-slate-500">Hóspedes</Label>
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        type="button"
                        data-testid="booking-guests-decrease"
                        onClick={() => setGuests(Math.max(1, guests - 1))}
                        className="w-8 h-8 rounded-full border border-slate-300 hover:bg-slate-100"
                      >
                        −
                      </button>
                      <span data-testid="booking-guests" className="text-sm w-8 text-center">{guests}</span>
                      <button
                        type="button"
                        data-testid="booking-guests-increase"
                        onClick={() => setGuests(Math.min(house.max_guests, guests + 1))}
                        className="w-8 h-8 rounded-full border border-slate-300 hover:bg-slate-100"
                      >
                        +
                      </button>
                      <span className="text-xs text-slate-500">de até {house.max_guests}</span>
                    </div>
                  </div>

                  {availability && (() => {
                    const avgNightly = availability.nights > 0
                      ? availability.base_total / availability.nights
                      : Number(house.base_price);
                    const seasonalApplied = Math.abs(avgNightly - Number(house.base_price)) > 0.5;
                    return (
                      <div className="mt-5 border-t border-slate-200 pt-4 space-y-2 text-sm" data-testid="booking-breakdown">
                        <Row
                          label={`${formatMoney(avgNightly)} × ${availability.nights} noite(s)`}
                          value={formatMoney(availability.base_total)}
                        />
                        {seasonalApplied && (
                          <div
                            className="text-[11px] text-[#0AA89C] bg-[#0AA89C]/5 border border-[#0AA89C]/20 rounded px-2 py-1.5 flex items-start gap-1"
                            data-testid="booking-seasonal-notice"
                          >
                            <span className="font-medium">Preço com sazonalidade aplicada.</span>
                            <span className="text-slate-600">Diária base R$ {Number(house.base_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} · média do período {formatMoney(avgNightly)}.</span>
                          </div>
                        )}
                        <Row label="Taxa de limpeza" value={formatMoney(availability.cleaning_fee)} />
                        <div className="border-t border-slate-200 pt-3 flex justify-between font-semibold">
                          <span>Total</span>
                          <span data-testid="booking-total">{formatMoney(availability.total)}</span>
                        </div>
                        {!availability.available && (
                          <div className="text-xs text-red-600 mt-2" data-testid="unavailable-msg">
                            Datas indisponíveis para esta casa.
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <Button
                    data-testid="reserve-btn"
                    onClick={() => setStep("guest")}
                    disabled={!availability?.available || checkingAvail}
                    className="w-full mt-5 bg-[#0AA89C] hover:bg-[#066B63] text-white"
                  >
                    {checkingAvail ? "Verificando…" : "Reservar"}
                  </Button>
                </>
              )}

              {step === "guest" && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitReservation();
                  }}
                  className="space-y-3"
                >
                  <div>
                    <Label>Nome completo *</Label>
                    <Input
                      data-testid="guest-name"
                      value={guestForm.name}
                      onChange={(e) => setGuestForm({ ...guestForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>E-mail *</Label>
                    <Input
                      data-testid="guest-email"
                      type="email"
                      value={guestForm.email}
                      onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>WhatsApp *</Label>
                    <Input
                      data-testid="guest-phone"
                      value={guestForm.phone}
                      onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })}
                      placeholder="(11) 99999-9999"
                      required
                    />
                  </div>
                  <div>
                    <Label>CPF (opcional)</Label>
                    <Input
                      data-testid="guest-document"
                      value={guestForm.document}
                      onChange={(e) => setGuestForm({ ...guestForm, document: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Adultos</Label>
                      <Input type="number" min={1} max={house.max_guests} value={adults} onChange={(e) => setAdults(e.target.value)} data-testid="guest-adults" />
                    </div>
                    <div>
                      <Label>Crianças</Label>
                      <Input type="number" min={0} value={children} onChange={(e) => setChildren(e.target.value)} data-testid="guest-children" />
                    </div>
                  </div>
                  <div>
                    <Label>Como conheceu a Azuhomes? (opcional)</Label>
                    <Input value={howHeard} onChange={(e) => setHowHeard(e.target.value)} placeholder="Instagram, indicação, Google…" data-testid="guest-how-heard" />
                  </div>
                  <label className="flex items-start gap-2 text-xs text-[#22302E]/80 pt-2 cursor-pointer" data-testid="lgpd-consent-wrapper">
                    <input
                      type="checkbox"
                      checked={lgpdConsent}
                      onChange={(e) => setLgpdConsent(e.target.checked)}
                      className="mt-0.5 accent-[#0AA89C]"
                      data-testid="lgpd-consent"
                    />
                    <span>
                      Autorizo o tratamento dos meus dados pessoais para a formalização desta reserva e para o envio das
                      comunicações relativas à estadia, conforme a <a href="/politica-privacidade" target="_blank" rel="noreferrer" className="underline">Política de Privacidade</a>.
                    </span>
                  </label>
                  <div className="border-t border-slate-200 pt-3 flex justify-between text-sm font-semibold">
                    <span>Total</span>
                    <span>{formatMoney(availability?.total || 0)}</span>
                  </div>
                  <Button
                    data-testid="submit-reservation"
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#0AA89C] hover:bg-[#066B63] text-white"
                  >
                    {submitting ? "Processando…" : "Ir para pagamento"}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setStep("select")}
                    className="w-full text-xs text-slate-500 hover:text-[#0AA89C]"
                  >
                    ← Voltar
                  </button>
                </form>
              )}
            </div>
          </aside>
        </div>
      </section>
    </PublicShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-slate-700">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
